import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';

import {
  setSelectedCompany,
  setUploadedFile,
  setUploadStatus,
  setErrorMessage,
  setUploadReport,
  clearUploadReport,
  resetUploadForm,
} from '../store/uploadSlice';
import {
  useUploadInventoryLoadMutation,
  useGetCompanyDetailsListByUserQuery,
  useLazyGetInventoryLoadDefaultQuery,
  useGetUserDetailsListByUserQuery
} from '../services/api';
import { useJobStream } from "../services/useJobStream";
import LoaderOverlay from "../components/LoaderOverlay";
import UploadDraftConfirmModal from '../components/UploadDraftConfirmModal';
import { selectPersonId } from '../store/userSlice';
const COMPANY_OPTIONS_FALLBACK = [
];

const LIST_BY_USER_ID = process.env.REACT_APP_COMPANY_LIST_USER_ID || '163776';

const WEBIM_ORIGINS = {
  dev: 'http://tccapplxd1:10132',
  test: 'https://webim2.qa.hibbertgroup.com',
  prod: 'https://webim.hibbertgroup.com',
};

/**
 * When REACT_APP_WEBIM_ENV is unset, infer tier (do not use NODE_ENV — production builds run on dev/test too).
 * Set REACT_APP_WEBIM_ENV=dev|test|prod explicitly for predictable links.
 */
function inferWebimTierWhenEnvUnset() {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname.toLowerCase();
    if (h.includes('qa')) return 'test';
    if (h.includes('dev') || h.includes('dev')) return 'dev';
    if (h === 'webim.hibbertgroup.com') return 'prod';
  }
  const hay = `${String(process.env.REACT_APP_BASE_URL || '')} ${String(process.env.REACT_APP_PROXY_TARGET || '')}`.toLowerCase();
  if (hay.includes('qa')) return 'test';
  if (hay.includes('dev') || hay.includes('demo.dev') || hay.includes('dev.webim')) return 'dev';
  if (hay.includes('webim.hibbertgroup.com')) return 'prod';
  return 'dev';
}

function resolveWebimTierKey() {
  const tier = String(process.env.REACT_APP_WEBIM_ENV || '').trim().toLowerCase();
  if (tier === 'test') return 'test';
  if (tier === 'prod' || tier === 'production') return 'prod';
  if (tier === 'dev' || tier === 'development') return 'dev';
  return inferWebimTierWhenEnvUnset();
}

/** Full WebIM home URL; override with REACT_APP_WEBIM_HOME_URL, or pick tier via REACT_APP_WEBIM_ENV (dev|test|prod). */
function resolveWebimHomeUrl() {
  const override = String(process.env.REACT_APP_WEBIM_HOME_URL || '').trim();
  if (override) return override;
  return `${WEBIM_ORIGINS[resolveWebimTierKey()]}/home.action`;
}

/** WebIM logout URL (session end); override with REACT_APP_WEBIM_LOGOUT_URL. */
function resolveWebimLogoutUrl() {
  const override = String(process.env.REACT_APP_WEBIM_LOGOUT_URL || '').trim();
  if (override) return override;
  return `${WEBIM_ORIGINS[resolveWebimTierKey()]}/logout.action`;
}

function isUnauthorizedQueryError(error) {
  return error != null && typeof error === 'object' && error.status === 401;
}

/** Same display shape as the Company / Division dropdown options. */
function formatCompanyDivisionDisplay(meta) {
  if (!meta) return '';
  const numPart = [meta.companyNumber, meta.companyDivisionNumber]
    .filter((x) => x !== '' && x != null)
    .join(' - ');
  const tailPart = [meta.label, meta.divisionName]
    .filter((x) => x !== '' && x != null)
    .join(' | ');
  if (numPart && tailPart) return `${numPart}\u00A0\u00A0${tailPart}`;
  return tailPart || numPart || meta.companyName || meta.label || '';
}

/** Maps GET /api/company-details/listByUser rows into select options + form field values. */
function companyPayloadToOptions(payload) {
  if (payload == null) return [];
  const list = Array.isArray(payload)
    ? payload
    : payload.data ?? payload.list ?? payload.items ?? payload.records ?? [];
  if (!Array.isArray(list)) return [];
  return list.map((item, idx) => {
    if (typeof item === 'string') {
      return {
        value: item,
        label: item,
        companyName: item,
        companyNumber: '',
        companyId: '',
        companyDivisionNumber: '',
        companyDivisionId: '',
        divisionName: ''
      };
    }
    const value =
      item.value ??
      item.code ??
      item.companyDivision ??
      item.companyCode ??
      item.divisionCode ??
      item.id ??
      `opt-${idx}`;
    const label =
      item.label ??
      item.name ??
      item.displayName ??
      item.companyName ??
      item.description ??
      String(value);
    return {
      value: String(value),
      label: String(label),
      companyName: item.companyName != null ? String(item.companyName) : String(label),
      companyNumber: item.companyNumber != null ? String(item.companyNumber) : '',
      companyId: item.companyId != null ? String(item.companyId) : '',
      companyDivisionNumber:
        item.companyDivisionNumber != null ? String(item.companyDivisionNumber) : '',
      companyDivisionId: item.companyDivisionId != null ? String(item.companyDivisionId) : '',
      divisionName: item?.divisionName
    };
  });
}

function mapUploadResponseToReport(data) {
  if (data == null) return null;
  const r = data.report ?? data.data ?? data;
  if (!r || typeof r !== 'object') return null;
  const failedRaw = r.failedRecords ?? r.failures ?? [];
  const failedRecords = Array.isArray(failedRaw)
    ? failedRaw.map((row) => ({
      row: row.row ?? row.rowNumber ?? row.line ?? 0,
      reason: row.reason ?? row.message ?? row.error ?? String(row),
    }))
    : [];
  return {
    successCount: Number(r.successCount ?? r.successfulRecords ?? r.success ?? 0),
    failedCount: Number(r.failedCount ?? r.failedRecordsCount ?? 0),
    failedRecords,
  };
}

const FILE_TYPE_ERROR =
  'File type is not supported. Please ensure file is in .xls format and re-upload.';

const FILE_FORMAT_OR_COLUMNS_ERROR =
  'File format is not supported. Please ensure the Item Upload List Template is in correct format with all included columns prior to upload.';

const FILE_SUCCESS = 'File successfully selected.';

const COMPANY_REQUIRED_MESSAGE = 'Please select a Company / Division before submitting.';

const DEFAULT_TEMPLATE_FILENAME = 'Item_Setup_Upload_List_Template.xlsx';

function filenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null;
  const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain?.[1] ? plain[1].replace(/"/g, '').trim() : null;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function validateFile(file) {
  if (!file) {
    return { ok: false, message: '' };
  }
  const lower = (file.name || '').toLowerCase();
  const isExcel = lower.endsWith('.xls') || lower.endsWith('.xlsx');
  if (!isExcel) {
    return { ok: false, message: FILE_TYPE_ERROR };
  }
  return { ok: true, message: '' };
}

/** When upload fails due to spreadsheet shape / template / columns (vs generic failure). */
function isUploadFormatOrColumnsError(err) {
  const status = err?.status ?? err?.originalStatus;
  if (status === 415) return true;

  const raw =
    err?.data?.message ??
    err?.data?.error ??
    (typeof err?.data === 'string' ? err.data : '');
  const msg = typeof raw === 'string' ? raw : '';
  const lower = msg.toLowerCase();
  if (!lower) return false;
  return /column|columns|format|template|header|missing|invalid.*(file|row|sheet|workbook)|parse|unable to read|spreadsheet|excel|schema|required field|workbook/i.test(
    lower,
  );
}

/** Best-effort message from RTK Query / fetchBaseQuery error `data` (422 bodies vary). */
function extractApiErrorMessage(err) {
  const data = err?.data;
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = data.errors[0];
      if (typeof first === 'string') return first.trim();
      if (first != null && typeof first === 'object' && typeof first.message === 'string') {
        return first.message.trim();
      }
      if (first != null && typeof first === 'object' && typeof first.defaultMessage === 'string') {
        return first.defaultMessage.trim();
      }
    }
  }
  return '';
}

function resolveUploadCatchMessage(err) {
  const status = err?.status ?? err?.originalStatus;
  if (status === 422) {
    const serverMsg = extractApiErrorMessage(err);
    return serverMsg || FILE_FORMAT_OR_COLUMNS_ERROR;
  }
  if (isUploadFormatOrColumnsError(err)) return FILE_FORMAT_OR_COLUMNS_ERROR;
  const msg = err?.data?.message || err?.error || err?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return 'Upload failed.';
}

export default function ItemSetupUploadList() {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const selectedFileRef = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [companyValidationError, setCompanyValidationError] = useState('');
  const [start, setStart] = useState(false);
  const selectedCompany = useSelector((s) => s.upload.selectedCompany);
  const uploadedFile = useSelector((s) => s.upload.uploadedFile);
  const uploadReport = useSelector((s) => s.upload.uploadReport);

  const [uploadInventoryLoad, { data: uploadResponse,  }] = useUploadInventoryLoadMutation();
  const streamUrl = useMemo(() => {
    if (!uploadResponse?.jobId) return null;
    return `/inventory/load/upload/jobs/${uploadResponse.jobId}/stream`;
  }, [uploadResponse?.jobId]);

  const { logs, status, jobStatus, progress, resetLogs } = useJobStream(streamUrl, {
    enabled: !!streamUrl && start,
    retryCount: 0, // 🚫 disable retry
  });
  const [fetchInventoryTemplate, { isFetching: isTemplateDownloading }] = useLazyGetInventoryLoadDefaultQuery();

  const skipCompanyList = process.env.NODE_ENV === 'test';

  const webImPersonId = useSelector(selectPersonId);
  const skipUserScopedQueries =
    skipCompanyList || !String(webImPersonId || '').trim();

  const {
    data: companyListPayload,
    isLoading: isCompanyListLoading,
    isError: isCompanyListError,
    error: companyListError,
  } = useGetCompanyDetailsListByUserQuery(
    skipUserScopedQueries ? skipToken : webImPersonId,
  );

  const {
    data: userData,
    isLoading: isUserDataLoading,
    isError: isUserDataError,
    error: userDetailsError,
  } = useGetUserDetailsListByUserQuery(skipUserScopedQueries ? skipToken : webImPersonId);

  useEffect(() => {
    if (skipCompanyList) return;
    const companyUnauthorized = isCompanyListError && isUnauthorizedQueryError(companyListError);
    const userUnauthorized = isUserDataError && isUnauthorizedQueryError(userDetailsError);
    if (!companyUnauthorized && !userUnauthorized) return;
    window.location.assign(resolveWebimLogoutUrl());
  }, [skipCompanyList, isCompanyListError, companyListError, isUserDataError, userDetailsError]);




  const companyOptions = useMemo(() => {
    const fromApi = companyPayloadToOptions(companyListPayload);
    return fromApi.length > 0 ? fromApi : COMPANY_OPTIONS_FALLBACK;
  }, [companyListPayload]);

  const selectedCompanyMeta = useMemo(
    () => companyOptions.find((o) => o.value === selectedCompany) ?? null,
    [companyOptions, selectedCompany],
  );
  const lastJobIdRef = useRef(null);
  const submitInFlightRef = useRef(false);
  const [uploadLocked, setUploadLocked] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (uploadResponse?.jobId && lastJobIdRef.current !== uploadResponse.jobId) {
      lastJobIdRef.current = uploadResponse.jobId;
      setStart(true); // 👈 trigger ONLY once per job
    }
  }, [uploadResponse?.jobId]);

  useEffect(() => {
    if (!uploadResponse?.jobId) return;
    if (status !== 'completed' && status !== 'error') return;
    if (!submitInFlightRef.current) return;
    selectedFileRef.current = null;
    dispatch(resetUploadForm());
    setFileInputKey((k) => k + 1);
    setStart(false);
    submitInFlightRef.current = false;
    setUploadLocked(false);
  }, [uploadResponse?.jobId, status, dispatch]);

  const handleCompanyChange = (e) => {
    setCompanyValidationError('');
    dispatch(setSelectedCompany(e.target.value));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    dispatch(setErrorMessage(''));
    if (!file) {
      selectedFileRef.current = null;
      dispatch(setUploadedFile(''));
      dispatch(clearUploadReport());
      return;
    }

    const result = validateFile(file);
    if (!result.ok) {
      selectedFileRef.current = null;
      dispatch(setUploadedFile(''));
      toast.error(result.message);
      return;
    }

    dispatch(clearUploadReport());
    selectedFileRef.current = file;
    dispatch(setUploadedFile(file.name));
    toast.success(FILE_SUCCESS);
  };

  const handleSubmitClick = () => {
    if (submitInFlightRef.current || uploadLocked) return;
    if (!selectedCompany || !selectedCompanyMeta) {
      setCompanyValidationError(COMPANY_REQUIRED_MESSAGE);
      toast.error(COMPANY_REQUIRED_MESSAGE);
      return;
    }
    if (!selectedFileRef.current || !uploadedFile) {
      const msg = 'Please upload a file before submitting.';
      dispatch(setErrorMessage(msg));
      toast.error(msg);
      return;
    }
    setConfirmOpen(true);
  };

  const performUploadSubmit = async () => {
    resetLogs();
    if (submitInFlightRef.current) return;
    if (!selectedCompany || !selectedCompanyMeta) {
      setCompanyValidationError(COMPANY_REQUIRED_MESSAGE);
      toast.error(COMPANY_REQUIRED_MESSAGE);
      return;
    }

    const file = selectedFileRef.current;
    const userDetails = userData ? userData[0] : [];
    if (!file) {
      const msg = 'Please upload a file before submitting.';
      dispatch(setErrorMessage(msg));
      toast.error(msg);
      return;
    }

    submitInFlightRef.current = true;
    flushSync(() => {
      setUploadLocked(true);
    });
    setCompanyValidationError('');
    dispatch(setErrorMessage(''));
    dispatch(setUploadStatus('idle'));
    try {
      const data = await uploadInventoryLoad({
        file,
        companyName: selectedCompanyMeta.companyName,
        companyNumber: selectedCompanyMeta.companyNumber,
        companyId: selectedCompanyMeta.companyId,
        companyDivisionNumber: selectedCompanyMeta.companyDivisionNumber,
        companyDivisionId: selectedCompanyMeta.companyDivisionId,
        appUserId: userDetails?.appUserId,
        personId: userDetails?.personId,
        firstName: userDetails?.firstName,
        lastName: userDetails?.lastName,
        emailAddress: userDetails?.emailAddress,
      }).unwrap();

      const report = mapUploadResponseToReport(data);
      if (report) {
        dispatch(setUploadReport(report));
      }
      
      if (!data?.jobId) {
        selectedFileRef.current = null;
        dispatch(resetUploadForm());
        setFileInputKey((k) => k + 1);
        submitInFlightRef.current = false;
        setUploadLocked(false);
      }      
      dispatch(setUploadStatus('success'));
    } catch (err) {
      dispatch(setUploadStatus('error'));
      const msg = resolveUploadCatchMessage(err);
      dispatch(setErrorMessage(msg));
      toast.error(msg);
      submitInFlightRef.current = false;
      setUploadLocked(false);
    }
  };

  const handleConfirmSubmit = () => {
    setConfirmOpen(false);
    void performUploadSubmit();
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  const triggerFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const bottomRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const { blob, contentDisposition } = await fetchInventoryTemplate().unwrap();
      const name = filenameFromContentDisposition(contentDisposition) || DEFAULT_TEMPLATE_FILENAME;
      triggerBlobDownload(blob, name);
      toast.success('Template downloaded.');
    } catch {
      toast.error('Could not download template from the server.');
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const uploadRow = Array.isArray(uploadResponse) ? uploadResponse[0] : uploadResponse;

  return (
    <div className="app-shell" id="top">
      <UploadDraftConfirmModal
        open={confirmOpen}
        fileName={uploadedFile || ''}
        companyDivisionLabel={formatCompanyDivisionDisplay(selectedCompanyMeta)}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />
      <Header userData={userData} />
      <main id="middle_container" className="app-main page-item-setup-upload">
        <p className="webim-return-heading">
          <a href={resolveWebimHomeUrl()} className="webim-return-link">
            Click Here to Return to WebIM
          </a>
        </p>
        <h1 className="page-title mt-10">Item Setup Upload List</h1>
        <div className="page-breadcrumb-row">
          <span className="page-hint">Upload item setup list from template.</span>
          <span className="page-breadcrumbs">
            <span className="crumb-orange">Item Setup</span>
            <span className="crumb-sep"> &gt;&gt; </span>
            <span className="crumb-blue">Upload List</span>
          </span>
        </div>
        {logs.length > 0 ? null :
          <LoaderOverlay
            show={
              status === 'connecting' ||
              status === 'streaming' ||
              (uploadLocked && status !== 'completed' && status !== 'error')
            }
            progress={progress}
            status={status}
          />
        }
        <section className="legacy-panel">
          <div className="legacy-panel-header">
            <span className="legacy-panel-title">Upload List Attributes</span>
          </div>
          <div className="legacy-panel-body">
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label" htmlFor="company-division">
                  Company / Division:
                </label>
                <select
                  id="company-division"
                  className="form-select"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  disabled={(isCompanyListLoading && !skipCompanyList) || uploadLocked || confirmOpen}
                  aria-busy={isCompanyListLoading && !skipCompanyList}
                  aria-invalid={Boolean(companyValidationError)}
                  aria-describedby={companyValidationError ? 'company-division-error' : undefined}
                >
                  <option value="">
                    {isCompanyListLoading && !skipCompanyList ? 'Loading companies…' : 'Select Company/Division'}
                  </option>
                  {companyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt?.companyNumber} - {opt?.companyDivisionNumber} {"\u00A0\u00A0"} {opt?.label} | {opt?.divisionName}
                    </option>
                  ))}
                </select>
                {isCompanyListError ? (
                  <p className="helper-text" role="status">
                    Could not load companies from the server; showing default list.
                  </p>
                ) : null}
                {companyValidationError ? (
                  <p id="company-division-error" className="form-error" role="alert">
                    {companyValidationError}
                  </p>
                ) : null}
              </div>

              <div className="form-field form-field-block">
                <div className="template-row">
                  <button
                    type="button"
                    className="btn btn-template"
                    onClick={handleDownloadTemplate}
                    disabled={isTemplateDownloading || uploadLocked || confirmOpen}
                    aria-busy={isTemplateDownloading}
                  >
                    {isTemplateDownloading ? 'Downloading…' : 'Download Item Setup Upload List Template'}
                  </button>
                </div>
                <p className="helper-text">Template includes all available item setup fields</p>
              </div>

              <div className="form-field form-field-block">
                <p className="upload-instructions">
                  Upload your item setup file here. Please ensure the file is a <strong>.xls</strong> and is in the format of the Item Setup Upload List Template. Any unsupported file types and formats will result in an error.
                </p>
                <input
                  key={fileInputKey}
                  ref={fileInputRef}
                  type="file"
                  className="file-input-hidden"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="btn btn-upload"
                  onClick={triggerFilePicker}
                  disabled={uploadLocked || confirmOpen}
                  aria-busy={uploadLocked}
                >
                  Upload File
                </button>
                {uploadedFile ? (
                  <p className="file-name-display">
                    Selected file: <span className="file-name-value">{uploadedFile}</span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <div className="submit-row">
          <button
            type="button"
            className="btn btn-submit"
            onClick={handleSubmitClick}
            disabled={!uploadedFile || uploadLocked || confirmOpen}
            aria-busy={uploadLocked}
          >
            Submit
          </button>
        </div>

        {logs.length > 0 && (
          <section className="upload-results-panel" aria-label="Upload processing results">
            <div className="upload-results-panel-header">
              <span className="upload-results-panel-title">Upload Results</span>
            </div>
            <div className="legacy-panel-body" style={{ height: "225px", overflowY: "auto" }}>
              {logs.map((row) => (
                <div key={row.recordOrdinal} className="upload-results-panel-body2">
                  <div className="upload-results-line flex items-center gap-2">

                    {/* Per-row loader (only while processing) */}

                    {/* Text */}
                    <div className="upload-results-label">
                      Excel Row no {row.excelRowNumber}{" "}
                      {row.success
                        ? "completed successfully"
                        : row.message
                          ? `failed: ${row.message}`
                          : "processing..."}
                    </div>

                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {jobStatus?.totalRecords ? (
              <div className="upload-results-panel-body">
                <p className="upload-summary-line">
                  <span className="upload-results-label">Number of Successful Records:</span>{' '}
                  <strong className="upload-results-value">
                    {jobStatus?.numberOfSuccessfulRecords ?? jobStatus?.numberOfSuccessfulRecords}
                  </strong>
                </p>
                <p className="upload-summary-line">
                  <span className="upload-results-label">Number of Failed Records:</span>{' '}
                  <strong className="upload-results-value">
                    {jobStatus?.numberOfFailedRecords ?? jobStatus?.numberOfFailedRecords}
                  </strong>
                </p>
                <p className="upload-summary-line">
                  <span className="upload-results-label">Total Records:</span>{' '}
                  <strong className="upload-results-value">
                    {jobStatus?.totalRecords ?? jobStatus?.totalRecords}
                  </strong>
                </p>
              </div>
            ) :
              <div className="upload-results-panel-body">
                <div className="upload-results-processing-row">
                  <span className="upload-results-label">Processing records</span>
                  <div className="typing-loader" aria-hidden />
                </div>
              </div>
            }
          </section>
        )}
        <div className="submit-row">
          {logs.length > 0 && (
            <button
              onClick={resetLogs}
              className="btn btn-submit"
              style={{ justifyContent: "flex-end", marginBlock: 10 }}
            >
              Clear Results
            </button>
          )}

        </div>
      </main>
      <Footer />

    </div>
  );
}
